<template>
  <div class="tables-wrapper">
    <div class="home-header">Доступы</div>

    <div class="products-wrapper">
      <div
          v-if="visibleActiveSubscriptions.length"
          class="dashboard-active-subscriptions"
      >
        <div class="dashboard-active-subscriptions__header">
          Активные подписки
        </div>

        <div class="dashboard-active-subscriptions__grid">
          <SubscriptionActiveCard
              v-for="subscription in visibleActiveSubscriptions"
              :key="subscription.title"
              :subscription="subscription"
              @order="showSubscriptionOrderModal"
          />
        </div>
      </div>

      <a-table
          :columns="getExtendedColumns('tests', 'Оценка ЭИ')"
          :class="getTableClass('tests')"
          rowKey="id"
          :data-source="tableData.tests?.children"
          :scroll="getTableScroll('tests')"
          :customHeaderRow="getTableHeaderRow('tests')"
          :pagination="false"
          :locale="{ emptyText: emptySlot() }"
          :table-layout="'fixed'"
      >
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'validUntil'">
            <a-popover
                v-if="hasMultipleOrderDates(record)"
                placement="topRight"
                overlayClassName="order-expiration-popover"
            >
              <template #content>
                <OrderExpirationListTable :orders="record?.orders ?? []" />
              </template>

              <span class="expiration-date-cell expiration-date-cell--interactive">
                <span>{{ record.validUntil }}</span>
                <ClockCircleOutlined
                    v-if="record.timeRemaining && record.timeRemaining < 30"
                    class="circle-clock"
                    :style="{ color: '#FAAD14' }"
                />
              </span>
            </a-popover>

            <span v-else class="expiration-date-cell">
              <span>{{ record.validUntil }}</span>
              <ClockCircleOutlined
                  v-if="record.timeRemaining && record.timeRemaining < 30"
                  class="circle-clock"
                  :style="{ color: '#FAAD14' }"
              />
            </span>
          </template>

          <template v-else>
            {{ text }}
          </template>
        </template>
      </a-table>

      <a-table
          :columns="getExtendedColumns('courses', 'Развитие')"
          :class="getTableClass('courses')"
          rowKey="id"
          :data-source="tableData.courses?.children"
          :scroll="getTableScroll('courses')"
          :customHeaderRow="getTableHeaderRow('courses')"
          :pagination="false"
          :locale="{ emptyText: emptySlot() }"
          :table-layout="'fixed'"
      >
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'validUntil'">
            <a-popover
                v-if="hasMultipleOrderDates(record)"
                placement="topRight"
                overlayClassName="order-expiration-popover"
            >
              <template #content>
                <OrderExpirationListTable :orders="record?.orders ?? []" />
              </template>

              <span class="expiration-date-cell expiration-date-cell--interactive">
                <span>{{ record.validUntil }}</span>
                <ClockCircleOutlined
                    v-if="record.timeRemaining && record.timeRemaining < 30"
                    class="circle-clock"
                    :style="{ color: '#FAAD14' }"
                />
              </span>
            </a-popover>

            <span v-else class="expiration-date-cell">
              <span>{{ record.validUntil }}</span>
              <ClockCircleOutlined
                  v-if="record.timeRemaining && record.timeRemaining < 30"
                  class="circle-clock"
                  :style="{ color: '#FAAD14' }"
              />
            </span>
          </template>

          <template v-else>
            {{ text }}
          </template>
        </template>
      </a-table>

      <a-table
          v-if="tableData?.tools"
          :columns="getShortListColumns('tools', 'Ежедневные помощники')"
          :class="getTableClass('tools')"
          rowKey="id"
          :data-source="tableData.tools"
          :scroll="getTableScroll('tools')"
          :customHeaderRow="getTableHeaderRow('tools')"
          :pagination="false"
          :locale="{ emptyText: emptySlot() }"
          :table-layout="'fixed'"
      />

      <a-table
          v-if="tableData.analytics?.children"
          :columns="getShortListColumns('analytics', 'Аналитика')"
          :class="getTableClass('analytics')"
          rowKey="id"
          :data-source="tableData.analytics?.children"
          :scroll="getTableScroll('analytics')"
          :customHeaderRow="getTableHeaderRow('analytics')"
          :pagination="false"
          :locale="{ emptyText: emptySlot() }"
          :table-layout="'fixed'"
      />

      <a-table
          v-if="tableData?.subscriptions"
          :columns="getShortListColumns('subscriptions', 'Подписки')"
          :class="getTableClass('subscriptions')"
          rowKey="id"
          :data-source="tableData.subscriptions"
          :scroll="getTableScroll('subscriptions')"
          :customHeaderRow="getTableHeaderRow('subscriptions')"
          :pagination="false"
          :locale="{ emptyText: emptySlot() }"
          :table-layout="'fixed'"
      />

      <div class="add-product-button">
        <a-button type="default" class="secondary-button" @click="showOrderModal">
          Заказать продукт
        </a-button>

        <a-button type="default" class="secondary-button" @click="showManageAccessModal">
          Управление доступами
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "@vue/runtime-core";
import useCheckRights from "../../mixins/useCheckRights";
import { useStore } from "vuex";
import PopUpForm from "@/components/Popups/PopUpForm";
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons-vue";
import { h } from "vue";
import StatusByPassing from "@/components/Cards/StatusByPassing.vue";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import ManageAccessForm from "@/components/Forms/Access/ManageAccessForm.vue";
import OrderExpirationListTable from "@/components/Tables/OrderExpirationListTable.vue";
import SubscriptionActiveCard from "@/views/Catalog/ProductCard/SubscriptionActiveCard.vue";
import catalogProducts from "@/views/Catalog/products.json";
import { resolveActiveCatalogSubscriptions } from "@/utils/catalogPricing";

export default {
  name: "DashboardProductTables",
  components: {
    SubscriptionActiveCard,
    OrderExpirationListTable,
    ManageAccessForm,
    StatusByPassing,
    PopUpForm,
    PlusCircleOutlined,
    MinusCircleOutlined,
    ClockCircleOutlined,
    PlusOutlined,
    MinusOutlined,
  },
  props: {
    tableData: Object,
  },
  emits: ["showOrderForm", "showManageAccessForm"],
  setup(props, context) {
    const store = useStore();
    const { getRight } = useCheckRights();

    const profile = computed(() => store.state.profile.profile);
    const companyActivityData = computed(() => store.state.companyActivity);
    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions ?? []);
    const dashboardSubscriptions = computed(() => props.tableData?.subscriptions ?? []);
    const dashboardPermittedSubscriptions = computed(() => {
      if (!dashboardSubscriptions.value.length) {
        return permittedSubscriptions.value;
      }

      return dashboardSubscriptions.value.map((subscription) => {
        const title = subscription.title;
        const permittedSubscription = permittedSubscriptions.value.find((item) => item.title === title);

        return {
          ...subscription,
          ...(permittedSubscription ?? {}),
          title,
          expired_at: permittedSubscription?.expired_at,
        };
      });
    });
    const visibleActiveSubscriptions = computed(() => resolveActiveCatalogSubscriptions(
        catalogProducts.activeSubscriptions,
        permittedSubscriptions.value,
    ));

    const expandedMap = ref({
      tests: true,
      courses: true,
      analytics: false,
      tools: false,
      subscriptions: false,
    });

    const renderedMap = ref({
      tests: true,
      courses: true,
      analytics: false,
      tools: false,
      subscriptions: false,
    });

    const toggleTable = (key) => {
      if (expandedMap.value[key]) {
        expandedMap.value[key] = false;

        setTimeout(() => {
          if (!expandedMap.value[key]) {
            renderedMap.value[key] = false;
          }
        }, 120);
      } else {
        renderedMap.value[key] = true;

        requestAnimationFrame(() => {
          expandedMap.value[key] = true;
        });
      }
    };

    const getTableHeaderRow = (tableKey) => () => ({
      class: "table-collapse-header-row",
      onClick: () => toggleTable(tableKey),
    });

    const tableScrollXMap = {
      tests: 650,
      courses: 650,
      tools: 760,
      analytics: 760,
      subscriptions: 760,
    };

    const getTableScroll = (key) => {
      if (!expandedMap.value[key]) {
        return undefined;
      }

      return {
        x: tableScrollXMap[key] ?? 650,
      };
    };

    const getTableClass = (key) => ({
      "dashboard-product-table": true,
      "dashboard-product-table--collapsed": !expandedMap.value[key],
      "dashboard-product-table--open":
          renderedMap.value[key] && expandedMap.value[key],
      "dashboard-product-table--closing":
          renderedMap.value[key] && !expandedMap.value[key],
      "dashboard-product-table--hidden": !renderedMap.value[key],
    });

    const columnsSubscription = [
      {
        title: "Действует до",
        dataIndex: "expires_at",
        key: "expires_at",
        width: 15,
        align: "center",
      },
    ];

    const emptySlot = () =>
        h("div", { class: "no-data-wrapper" }, [
          h("img", { src: NotFoundIcon, class: "no-data-image" }),
          h("div", { class: "no-data-text" }, "Нет данных для отображения"),
        ]);

    const renderClockIcon = ({ record }) => {
      const children = [h("span", record.validUntil)];

      if (record.timeRemaining && record.timeRemaining < 30) {
        children.push(
            h(ClockCircleOutlined, {
              class: "circle-clock",
              style: {
                color: "#FAAD14",
              },
            })
        );
      }

      return h(
          "div",
          {
            class: "expiration-date-cell",
          },
          children
      );
    };

    const hasMultipleOrderDates = (record) =>
        record?.orders && record.orders.length > 1;

    const renderCollapseElement = (key) =>
        h(
            "span",
            {
              class: [
                "custom-collapse-expand-icon",
                expandedMap.value[key]
                    ? "custom-collapse-expand-icon--active"
                    : "",
              ],
              onClick: (e) => {
                e.stopPropagation();
                toggleTable(key);
              },
            },
            [h(expandedMap.value[key] ? MinusOutlined : PlusOutlined)]
        );

    const getShortListColumns = (key, mainTitle) => {
      return [
        {
          title: () => renderCollapseElement(key),
          dataIndex: "toggle",
          key: "toggle",
          width: 28,
          align: "center",
          className: "collapse-toggle-cell",
          customHeaderCell: () => ({ class: "collapse-toggle-cell" }),
          customCell: () => ({ class: "collapse-toggle-cell" }),
        },
        {
          title: mainTitle,
          dataIndex: "product",
          key: "product",
          width: 550,
          align: "left",
        },
        {
          title: "Доступ",
          dataIndex: "has_access",
          key: "has_access",
          width: 70,
          align: "right",
        },
        {
          title: "Истекает",
          dataIndex: "validUntil",
          key: "validUntil",
          width: 100,
          align: "center",
          customRender: renderClockIcon,
        },
      ];
    };

    const getExtendedColumns = (key, mainTitle) => {
      return [
        {
          title: () => renderCollapseElement(key),
          dataIndex: "toggle",
          key: "toggle",
          width: 28,
          align: "center",
          className: "collapse-toggle-cell",
          customHeaderCell: () => ({ class: "collapse-toggle-cell" }),
          customCell: () => ({ class: "collapse-toggle-cell" }),
        },
        {
          title: mainTitle,
          dataIndex: "product",
          key: "product",
          width: 130,
          align: "left",
        },
        {
          title: "Куплено",
          dataIndex: "purchased",
          key: "purchased",
          width: 80,
          align: "right",
        },
        {
          title: "Выдано",
          dataIndex: "issued",
          key: "issued",
          width: 80,
          align: "right",
        },
        {
          title: "Осталось",
          dataIndex: "remaining",
          key: "remaining",
          width: 80,
          align: "right",
        },
        {
          title: "Завершено",
          dataIndex: "completed",
          key: "completed",
          width: 100,
          align: "right",
        },
        {
          title: "Истекает",
          dataIndex: "validUntil",
          key: "validUntil",
          width: 100,
          align: "center",
        },
      ];
    };

    const showOrderModal = () => {
      context.emit("showOrderForm");
    };

    const showSubscriptionOrderModal = () => {
      context.emit("showOrderForm", "subscriptions");
    };

    const showManageAccessModal = () => {
      context.emit("showManageAccessForm");
    };

    return {
      getRight,
      profile,
      showOrderModal,
      showSubscriptionOrderModal,
      companyActivityData,
      catalogProducts,
      emptySlot,
      columnsSubscription,
      getTableHeaderRow,
      getTableScroll,
      getTableClass,
      getExtendedColumns,
      getShortListColumns,
      hasMultipleOrderDates,
      showManageAccessModal,
      visibleActiveSubscriptions,
    };
  },
};
</script>

<style scoped lang="scss">
.collapse-table-header {
  display: flex;
  justify-content: space-between;
}

:deep(.custom-collapse-expand-icon) {
  margin: auto;
  display: flex;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  width: 18px;
  min-width: 18px;
  max-width: 18px;
  height: 18px;
  min-height: 18px;
  max-height: 18px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  background: #fff;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
  color: var(--main-font);
}

:deep(.custom-collapse-expand-icon .anticon) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  line-height: 1;
}

:deep(.custom-collapse-expand-icon svg) {
  display: block;
  width: 12px;
  height: 12px;
}

:deep(.custom-collapse-expand-icon--active) {
  transform: translateY(1px);
}

.expiration-date-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 2px;
  flex-direction: row-reverse;
  line-height: 1.2;
}

.expiration-date-cell--interactive {
  cursor: pointer;
}

.collapse-header {
  font-size: 14px;
  font-weight: 500;
  text-align: start;
  width: 580px;
}

.collapse-columns {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--neutral-text-description);
}

.collapse-columns > .collapse-column {
  width: 70px;
}

.collapse-columns > .collapse-column:last-child {
  width: 85px;
}

:deep(.ant-collapse-header) {
  padding: 5px 15px !important;
}

.ant-collapse {
  border: none !important;
}

.ant-collapse-item {
  border-bottom: none !important;
}

.ant-collapse-header {
  border-bottom: none !important;
}

.ant-collapse-content {
  border-top: none !important;
  border-bottom: none !important;
}

.ant-collapse-content-box {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  border: none !important;
}

:deep(.ant-collapse-content-box) {
  padding: 0 !important;
}

.products-wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 15px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;

  :deep(.ant-table-cell) {
    border-bottom: unset;
  }

  :deep(table > thead > tr:first-child > *:last-child) {
    border-start-end-radius: 8px;
    border-end-end-radius: 8px;
  }

  :deep(table > thead > tr:first-child > *:first-child) {
    border-start-start-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  :deep(.ant-table-placeholder > .ant-table-cell) {
    text-align: center !important;
  }

  :deep(.no-data-wrapper) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 24px;
    text-align: center;
  }
}

.dashboard-active-subscriptions {
  margin-bottom: 5px;
}

.dashboard-active-subscriptions__header {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 120%;
}

.dashboard-active-subscriptions__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: $tablet-breakpoint) {
    grid-template-columns: 1fr;
  }
}

.add-product-button {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  column-gap: 15px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
    row-gap: 8px;
  }
}

:deep(.ant-table-thead > tr > th) {
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  background-color: #fafafa;
}

:deep(.ant-table-tbody > tr > td) {
  text-align: center;
  vertical-align: middle;
  padding: 10px 8px;
  font-size: 14px;
}

:deep(.ant-table-tbody > tr > td:first-child) {
  text-align: left;
}

:deep(.ant-table-thead > tr > th) {
  padding: 5px !important;
  color: var(--neutral-text-description);
}

:deep(.ant-table-thead > tr > th:not(:first-child)) {
  padding: 5px !important;
  color: var(--neutral-text-description);
  font-weight: 400;
}

:deep(.ant-table-thead > tr > th:nth-child(2)) {
  color: var(--dp-text-color);
}

:deep(.collapse-toggle-cell) {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
  padding-right: 7px !important;
  padding-left: 3px !important;
  text-align: center !important;
  overflow: visible !important;
  white-space: nowrap !important;
}

:deep(.table-collapse-header-row),
:deep(.table-collapse-header-row > th) {
  cursor: pointer;
}

:deep(.dashboard-product-table .ant-table-tbody) {
  transform-origin: top;
}

:deep(.dashboard-product-table--open .ant-table-tbody) {
  display: table-row-group;
  animation: tableBodyOpen 0.2s ease;
}

:deep(.dashboard-product-table--closing .ant-table-tbody) {
  display: table-row-group;
  animation: tableBodyClose 0.12s ease forwards;
}

:deep(.dashboard-product-table--hidden .ant-table-tbody) {
  display: none;
}

:deep(.dashboard-product-table--collapsed .ant-table-container),
:deep(.dashboard-product-table--collapsed .ant-table-content),
:deep(.dashboard-product-table--collapsed .ant-table-body) {
  overflow-x: hidden !important;
  scrollbar-width: none;
}

:deep(.dashboard-product-table--collapsed .ant-table-container::-webkit-scrollbar),
:deep(.dashboard-product-table--collapsed .ant-table-content::-webkit-scrollbar),
:deep(.dashboard-product-table--collapsed .ant-table-body::-webkit-scrollbar) {
  display: none;
  width: 0;
  height: 0;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 8px !important;
}

:deep(.ant-table-tbody > .ant-table-measure-row > td) {
  padding: 0 !important;
}

.home-header {
  margin-bottom: 20px;
  font-size: 20px;
  text-align: left;
  font-weight: 500;
}

.tables-wrapper {
  width: 100%;
}

@media (max-width: $mobile-breakpoint) {
  .products-wrapper {
    padding: 12px;
  }

  :deep(.ant-table-cell) {
    font-size: 12px;
  }

  :deep(.collapse-toggle-cell) {
    width: 30px !important;
    min-width: 30px !important;
    max-width: 30px !important;
    padding-right: 7px !important;
    padding-left: 3px !important;
  }

  :deep(.custom-collapse-expand-icon) {
    flex-basis: 18px;
    width: 18px;
    min-width: 18px;
    height: 18px;
    min-height: 18px;
  }

  .home-header {
    padding: 0 20px;
  }
}
</style>
